import React from 'react';
import { ErrorPage } from './pages/ErrorPage';
import { logValidationError } from '@shared/validation/validation.helpers';
import {
  getErrorRecoveryMode,
  hasRecentSameErrorFor,
  recordAndReload,
} from './helpers/error-boundary.helpers';
import {
  isViteHotReloadEnabled,
  subscribeViteHotReload,
} from './helpers/vite.helpers';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  /**
   * Shown instead of {@link ErrorPage} / the HMR Retry control. Omit at the app
   * root so production still uses the error page after a failed recovery reload.
   */
  fallback?: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

/**
 * App-root crash boundary. Local Vite HMR shows Retry and clears on the next
 * Fast Refresh instead of a full reload (that reload used to drop Auth0 memory
 * cache and then lock Unknown Error for two minutes). Production still reloads
 * once, then shows {@link ErrorPage} if the same error repeats.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private unsubscribeHotReload?: () => void;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // HMR: keep a fallback until the next Fast Refresh. Production: only lock
    // the error page after a recent same-error reload (see recordAndReload).
    const hasError =
      getErrorRecoveryMode() === 'hmr' || hasRecentSameErrorFor(error);
    return { hasError, error };
  }

  componentDidMount(): void {
    this.unsubscribeHotReload = subscribeViteHotReload(this.resetErrorState);
  }

  componentWillUnmount(): void {
    this.unsubscribeHotReload?.();
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logError(error, errorInfo);
    if (!hasRecentSameErrorFor(error)) recordAndReload(error);
  }

  /** Clear a Fast Refresh render failure so the next HMR can mount again. */
  private readonly resetErrorState = (): void => {
    if (!this.state.hasError) return;
    this.setState({ hasError: false, error: undefined });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      logError(this.state.error);
      if (this.props.fallback) return this.props.fallback;
      if (isViteHotReloadEnabled()) {
        return (
          <button type="button" onClick={this.resetErrorState}>
            Retry
          </button>
        );
      }
      return <ErrorPage />;
    }

    return this.props.children;
  }
}

type IsolatedErrorBoundaryFallback = (reset: () => void) => React.ReactNode;

type IsolatedErrorBoundaryProps = {
  children: React.ReactNode;
  /**
   * Rendered after a caught error. Receives a reset callback so the widget can
   * be shown again.
   */
  fallback?: IsolatedErrorBoundaryFallback;
  onError?: (error: Error) => void;
};

type IsolatedErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Catches render errors in a subtree without reloading the app or showing the
 * full error page. Use around optional widgets (e.g. image generation).
 * Call the fallback `reset` argument to remount children after a failure.
 */
export class IsolatedErrorBoundary extends React.Component<
  IsolatedErrorBoundaryProps,
  IsolatedErrorBoundaryState
> {
  constructor(props: IsolatedErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): IsolatedErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('IsolatedErrorBoundary caught an error:', error, errorInfo);
    try {
      this.props.onError?.(error);
    } catch {
      // Keep the failure inside this widget.
    }
  }

  /** Clear the error so children render again. */
  private readonly reset = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);
    return (
      <button type="button" onClick={this.reset}>
        Retry
      </button>
    );
  }
}

/**
 * Log an error to the console.
 * @param error - The error object.
 * @param errorInfo - The error info object.
 */
const logError = (error: Error | undefined, errorInfo?: React.ErrorInfo) => {
  if (error) {
    // Log validation error OR just console.error it if not a validation error
    logValidationError(error) ??
      console.error('ErrorBoundary caught an error:', error, errorInfo);
  }
};
